import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Box, FormControl } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PATTERNS } from '../../../utils/constans/constans';
import PrimaryBtn from '../../shared/Buttons/PrimaryButton';
import { FormControlsWrapper } from '../../styled/Form/FormControlsWrapper';
import TextFieldStyled from '../../shared/TextFieldStyled';
import useAddToCompanyDialog from '../../../hooks/useAddToCompanyDialog';
import useAdminApi from '../../../hooks/admin/useAdminApi';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import useUser from '../../../hooks/user/useUser';

const schema = yup.object().shape({
    email: yup
        .string()
        .required('FORM_VALIDATION_ERROR.REQUIRED')
        .matches(PATTERNS.VALID_EMAIL, 'FORM_VALIDATION_ERROR.EMAIL'),
    first_name: yup
        .string()
        .required('FORM_VALIDATION_ERROR.REQUIRED')
        .min(3, 'FORM_VALIDATION_ERROR.MIN_CHARACTER_3'),
    last_name: yup
        .string()
        .required('FORM_VALIDATION_ERROR.REQUIRED')
        .min(3, 'FORM_VALIDATION_ERROR.MIN_CHARACTER_3'),
});

export const AddToCompanyForm: React.FC<{ submitSuccess: () => void }> = (
    props
) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
    });
    const { submitSuccess } = props;

    const { t } = useTranslation();
    const adminApi = useAdminApi();
    const user = useUser();
    const addToCompanyDialog = useAddToCompanyDialog();

    const [submitIsLoading, setSubmitIsLoading] = useState(false);

    const makeRequestBody = (data) => {
        return {
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            bc_company_number:
                addToCompanyDialog.addToCompanyDialogState.company.id,
        };
    };

    const onSubmit: SubmitHandler<FormValues> = (data) => {
        setSubmitIsLoading(true);
        if (addToCompanyDialog.addToCompanyDialogState.company.isAdmin) {
            adminApi
                .addToCompany(makeRequestBody(data))
                .then((res) => {
                    SnackbarUtils.success(
                        t('NOTIFICATION.ADDED_SUCCESSFULLY').toString()
                    );
                    submitSuccess();
                })
                .catch((err) => {
                    setSubmitIsLoading(false);
                });
        } else {
            user.addToCompany(makeRequestBody(data))
                .then((res) => {
                    SnackbarUtils.success(
                        t('NOTIFICATION.ADDED_SUCCESSFULLY').toString()
                    );
                    submitSuccess();
                })
                .catch((err) => {
                    setSubmitIsLoading(false);
                });
        }
    };

    const getTranslate = (key) => {
        return t(key).toString();
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <>
                    {/* Email address */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            type="email"
                            error={!!errors.email}
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.EMAIL'
                            ).toString()}
                            {...register('email')}
                            helperText={
                                errors.email
                                    ? getTranslate(errors.email.message)
                                    : ' '
                            }
                        />
                    </FormControl>
                    <FormControlsWrapper>
                        {/* Last Name */}
                        <FormControl fullWidth>
                            <TextFieldStyled
                                label={t(
                                    'PROFILE.PROFILE_DIALOG.FORM_CONTENT.LAST_NAME'
                                ).toString()}
                                error={!!errors.last_name}
                                {...register('last_name')}
                                helperText={
                                    errors.last_name
                                        ? getTranslate(errors.last_name.message)
                                        : ' '
                                }
                            />
                        </FormControl>
                        {/* First Name */}
                        <FormControl fullWidth>
                            <TextFieldStyled
                                label={t(
                                    'PROFILE.PROFILE_DIALOG.FORM_CONTENT.FIRST_NAME'
                                ).toString()}
                                error={!!errors.first_name}
                                {...register('first_name')}
                                helperText={
                                    errors.first_name
                                        ? getTranslate(
                                              errors.first_name.message
                                          )
                                        : ' '
                                }
                            />
                        </FormControl>
                    </FormControlsWrapper>
                </>
                <Box
                    sx={{
                        justifyContent: 'center',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <PrimaryBtn type="submit" disabled={submitIsLoading}>
                        {t(
                            'PROFILE.PROFILE_DIALOG.FORM_CONTENT.SEND'
                        ).toString()}
                    </PrimaryBtn>
                </Box>
            </form>
        </>
    );
};

type FormValues = {
    email: string;
    first_name: string;
    last_name: string;
};
